import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Cube component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconCube extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M11.999 8.91504C12.4132 8.91504 12.749 9.25083 12.749 9.66504V22.0877C12.749 22.5019 12.4132 22.8377 11.999 22.8377C11.5848 22.8377 11.249 22.5019 11.249 22.0877V9.66504C11.249 9.25083 11.5848 8.91504 11.999 8.91504Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M11.9987 1.16235C11.8053 1.16235 11.6138 1.20099 11.4357 1.27593L2.11589 4.86404C1.88371 4.93362 1.67186 5.05911 1.4991 5.22981C1.48119 5.2475 1.46419 5.26608 1.44816 5.28548C1.23 5.54939 1.10871 5.88004 1.10456 6.22242L1.10449 17.6554L1.10462 17.6651C1.10859 17.9707 1.204 18.2682 1.37856 18.5191C1.55312 18.7701 1.7993 18.9631 2.08449 19.0732L11.4483 22.6782C11.4948 22.6961 11.5428 22.7093 11.5918 22.7176C11.7089 22.7934 11.8486 22.8374 11.9985 22.8374C12.1484 22.8374 12.2881 22.7935 12.4052 22.7177C12.4543 22.7094 12.5024 22.6962 12.5491 22.6782L21.9133 19.073C22.1985 18.963 22.4442 18.7701 22.6188 18.5191C22.7934 18.2682 22.8888 17.9707 22.8928 17.6651V6.31522C22.9106 5.94794 22.7928 5.58676 22.5616 5.30058C22.3947 5.08107 22.1698 4.91229 21.9121 4.81339L12.5593 1.27493C12.3818 1.20065 12.1912 1.16235 11.9987 1.16235ZM12.7485 20.9941L21.3735 17.6735C21.379 17.6713 21.384 17.6674 21.3874 17.6625C21.3906 17.658 21.3924 17.6527 21.3928 17.6472V6.85238L12.7485 10.1802V20.9941ZM20.1146 5.73712L12.0142 2.67246L11.9987 2.66639L11.9872 2.6709L3.95273 5.76419L11.9985 8.86163L20.1146 5.73712ZM2.60456 6.85249V17.6472C2.60492 17.6527 2.60677 17.658 2.60992 17.6625C2.61333 17.6674 2.61813 17.6712 2.62369 17.6734L11.2485 20.994V10.1802L2.60456 6.85249Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-cube') === undefined) {
  customElements.define('cat-icon-cube', BlocksIconCube);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-cube': BlocksIconCube;
  }
}
